import React, { useEffect, useState, useCallback, useRef } from 'react';
import AppLayout from '../../components/AppLayout';
import {
  listExpertViews,
  getExpertViewMeta,
  createExpertView,
  updateExpertView,
  deleteExpertView,
  getImageUrl,
} from '../../api/expertViewService';
import './ExpertViewAdmin.css';

const INITIAL_FORM = {
  title: '',
  description: '',
  symbol: '',
  recordPrice: '',
  entryPrice: '',
  target: '',
  stoploss: '',
  strategyCategory: 'other',
  analysisType: 'long',
  tags: '',
  isPublished: true,
};

const fmtPrice = (v) =>
  v == null || v === ''
    ? '—'
    : Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '';

const ExpertViewAdmin = () => {
  const [meta, setMeta] = useState({ strategyCategories: [], analysisTypes: [] });
  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [form, setForm] = useState(INITIAL_FORM);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text }

  const fileInputRef = useRef(null);

  // Auto-dismiss success toast after 3.5s
  useEffect(() => {
    if (!message || message.type !== 'success') return undefined;
    const t = setTimeout(() => setMessage(null), 3500);
    return () => clearTimeout(t);
  }, [message]);

  // ── Load meta + initial list ─────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoadingList(true);
    try {
      const [metaRes, listRes] = await Promise.all([
        getExpertViewMeta(),
        listExpertViews({ limit: 50, includeUnpublished: 'true' }),
      ]);
      setMeta({
        strategyCategories: metaRes.data.strategyCategories || [],
        analysisTypes: metaRes.data.analysisTypes || [],
      });
      setItems(listRes.data.items || []);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || err.message });
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── File preview ─────────────────────────────────────────────
  useEffect(() => {
    if (!file) { setPreviewUrl(''); return; }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // ── Form helpers ────────────────────────────────────────────
  const onField = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0] || null;
    if (f && !/^image\/(jpeg|jpg|png|webp)$/i.test(f.type)) {
      setMessage({ type: 'error', text: 'Only JPG, PNG, or WEBP files are allowed.' });
      return;
    }
    if (f && f.size > 8 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'File too large. Max 8 MB.' });
      return;
    }
    setFile(f);
  };

  const resetForm = () => {
    setForm(INITIAL_FORM);
    setFile(null);
    setPreviewUrl('');
    setEditingId(null);
    setProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const startEdit = (item) => {
    setEditingId(item._id);
    setForm({
      title: item.title || '',
      description: item.description || '',
      symbol: item.symbol || '',
      recordPrice: item.recordPrice ?? '',
      entryPrice: item.entryPrice ?? '',
      target: item.target ?? '',
      stoploss: item.stoploss ?? '',
      strategyCategory: item.strategyCategory || 'other',
      analysisType: item.analysisType || 'long',
      tags: (item.tags || []).join(', '),
      isPublished: !!item.isPublished,
    });
    setFile(null);
    setPreviewUrl(getImageUrl(item.imageUrl));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── Submit ──────────────────────────────────────────────────
  const onSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (!form.title.trim() || !form.symbol.trim() || !form.analysisType) {
      setMessage({ type: 'error', text: 'Title, Symbol, and Analysis Type are required.' });
      return;
    }

    setUploading(true);
    try {
      if (editingId) {
        // Metadata-only update
        const body = {
          ...form,
          tags: form.tags
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean),
        };
        const { data } = await updateExpertView(editingId, body);
        setItems((arr) => arr.map((x) => (x._id === editingId ? data.expertView : x)));
        setMessage({ type: 'success', text: '✨ Chart updated successfully.' });
        resetForm();
      } else {
        if (!file) {
          setMessage({ type: 'error', text: 'Please choose a chart image.' });
          setUploading(false);
          return;
        }
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => {
          if (k === 'tags') {
            const tags = v.split(',').map((t) => t.trim()).filter(Boolean);
            tags.forEach((t) => fd.append('tags[]', t));
          } else {
            fd.append(k, v);
          }
        });
        fd.append('image', file);

        const { data } = await createExpertView(fd, (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded * 100) / evt.total));
        });

        setItems((arr) => [data.expertView, ...arr]);
        setMessage({ type: 'success', text: '🎉 Chart successfully shared!' });
        resetForm();
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || err.message });
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Delete this chart? This cannot be undone.')) return;
    try {
      await deleteExpertView(id);
      setItems((arr) => arr.filter((x) => x._id !== id));
      if (editingId === id) resetForm();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || err.message });
    }
  };

  const onTogglePublish = async (item) => {
    try {
      const { data } = await updateExpertView(item._id, { isPublished: !item.isPublished });
      setItems((arr) => arr.map((x) => (x._id === item._id ? data.expertView : x)));
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || err.message });
    }
  };

  return (
    <AppLayout pageBadge="Expertview Admin">
      <div className="eva-page">
        <div className="eva-header">
          <div>
            <h1 className="eva-title">📤 Publish Chart Analysis</h1>
            <p className="eva-subtitle">
              Share annotated charts with users — fill in the trade plan and upload the image.
            </p>
          </div>
        </div>

        {message && (
          <div className={`eva-toast eva-toast--${message.type}`}>
            {message.type === 'success' ? '✅' : '❌'} {message.text}
            <button onClick={() => setMessage(null)} aria-label="Dismiss">✕</button>
          </div>
        )}

        {/* ── Upload form ───────────────────────────────────── */}
        <form className="eva-form glass-card" onSubmit={onSubmit}>
          <div className="eva-form-head">
            <h2>{editingId ? '✏️ Edit chart' : '🆕 New chart analysis'}</h2>
            {editingId && (
              <button type="button" className="eva-btn-ghost" onClick={resetForm}>
                Cancel edit
              </button>
            )}
          </div>

          <div className="eva-form-grid">
            {/* Left: image picker + preview */}
            <div className="eva-form-left">
              <label className="eva-field">
                <span>Chart Image (JPG / PNG / WEBP)</span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={onPickFile}
                  disabled={!!editingId}
                />
                {editingId && (
                  <small className="eva-hint">
                    Image cannot be replaced from edit. Delete and re-upload to change.
                  </small>
                )}
              </label>

              <div className="eva-preview">
                {previewUrl ? (
                  <img src={previewUrl} alt="Chart preview" />
                ) : (
                  <div className="eva-preview-empty">
                    <span>🖼️</span>
                    <p>No image selected</p>
                  </div>
                )}
              </div>

              {uploading && progress > 0 && (
                <div className="eva-progress">
                  <div className="eva-progress-bar" style={{ width: `${progress}%` }} />
                  <span>{progress}%</span>
                </div>
              )}
            </div>

            {/* Right: meta fields */}
            <div className="eva-form-right">
              <div className="eva-row-2">
                <label className="eva-field">
                  <span>Title *</span>
                  <input
                    type="text"
                    name="title"
                    value={form.title}
                    onChange={onField}
                    placeholder="e.g. Reliance breakout setup"
                    required
                  />
                </label>

                <label className="eva-field">
                  <span>Symbol *</span>
                  <input
                    type="text"
                    name="symbol"
                    value={form.symbol}
                    onChange={onField}
                    placeholder="RELIANCE"
                    required
                    style={{ textTransform: 'uppercase' }}
                  />
                </label>
              </div>

              <div className="eva-row-3">
                <label className="eva-field">
                  <span>Strategy</span>
                  <select name="strategyCategory" value={form.strategyCategory} onChange={onField}>
                    {meta.strategyCategories.map((c) => (
                      <option key={c} value={c}>
                        {c.replace(/([a-z])([A-Z])/g, '$1 $2')}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="eva-field">
                  <span>Analysis type *</span>
                  <div className="eva-type-toggle">
                    {(meta.analysisTypes.length ? meta.analysisTypes : ['long', 'short', 'neutral']).map((t) => (
                      <button
                        key={t}
                        type="button"
                        className={`eva-type-btn eva-type-btn--${t} ${form.analysisType === t ? 'active' : ''}`}
                        onClick={() => setForm((f) => ({ ...f, analysisType: t }))}
                      >
                        {t === 'long' ? '↗ Long' : t === 'short' ? '↘ Short' : '◎ Neutral'}
                      </button>
                    ))}
                  </div>
                </div>

                <label className="eva-field eva-field--toggle">
                  <span>Visibility</span>
                  <label className="eva-switch">
                    <input
                      type="checkbox"
                      name="isPublished"
                      checked={form.isPublished}
                      onChange={onField}
                    />
                    <span className="eva-switch-slider" />
                    <span className="eva-switch-label">{form.isPublished ? 'Published' : 'Draft'}</span>
                  </label>
                </label>
              </div>

              <div className="eva-row-4">
                <label className="eva-field">
                  <span>Record price</span>
                  <input type="number" step="0.01" name="recordPrice" value={form.recordPrice} onChange={onField} placeholder="0.00" />
                </label>
                <label className="eva-field">
                  <span>Entry price</span>
                  <input type="number" step="0.01" name="entryPrice" value={form.entryPrice} onChange={onField} placeholder="0.00" />
                </label>
                <label className="eva-field">
                  <span>Target</span>
                  <input type="number" step="0.01" name="target" value={form.target} onChange={onField} placeholder="0.00" />
                </label>
                <label className="eva-field">
                  <span>Stop-loss</span>
                  <input type="number" step="0.01" name="stoploss" value={form.stoploss} onChange={onField} placeholder="0.00" />
                </label>
              </div>

              <label className="eva-field">
                <span>Tags (comma-separated)</span>
                <input
                  type="text"
                  name="tags"
                  value={form.tags}
                  onChange={onField}
                  placeholder="breakout, weekly, momentum"
                />
              </label>

              <label className="eva-field">
                <span>Description / Notes</span>
                <textarea
                  name="description"
                  rows={4}
                  value={form.description}
                  onChange={onField}
                  placeholder="Explain the trade idea, levels, risk-reward, timeframe…"
                />
              </label>

              <div className="eva-form-actions">
                <button type="submit" className="eva-btn-primary" disabled={uploading}>
                  {uploading ? '⏳ Publishing…' : editingId ? '💾 Save changes' : '🚀 Publish chart'}
                </button>
                <button type="button" className="eva-btn-ghost" onClick={resetForm} disabled={uploading}>
                  Reset
                </button>
              </div>
            </div>
          </div>
        </form>

        {/* ── Existing charts ──────────────────────────────── */}
        <div className="eva-list-head">
          <h2>📚 Published charts ({items.length})</h2>
          <button className="eva-btn-ghost" onClick={fetchAll}>🔄 Refresh</button>
        </div>

        {loadingList ? (
          <div className="eva-loading">Loading…</div>
        ) : items.length === 0 ? (
          <div className="eva-empty glass-card">
            <p>No charts uploaded yet. Publish your first one above.</p>
          </div>
        ) : (
          <div className="eva-table-wrap glass-card">
            <table className="eva-table">
              <thead>
                <tr>
                  <th>Preview</th>
                  <th>Symbol / Title</th>
                  <th>Type</th>
                  <th>Strategy</th>
                  <th>Entry / Target / SL</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Stats</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it._id}>
                    <td>
                      <img
                        src={getImageUrl(it.imageUrl)}
                        alt={it.title}
                        className="eva-thumb"
                        loading="lazy"
                      />
                    </td>
                    <td>
                      <div className="eva-symbol">{it.symbol}</div>
                      <div className="eva-title-cell">{it.title}</div>
                    </td>
                    <td>
                      <span className={`eva-pill eva-pill--${it.analysisType}`}>
                        {it.analysisType}
                      </span>
                    </td>
                    <td className="eva-mono">{it.strategyCategory}</td>
                    <td className="eva-prices">
                      <span>E: {fmtPrice(it.entryPrice)}</span>
                      <span className="eva-pos">T: {fmtPrice(it.target)}</span>
                      <span className="eva-neg">SL: {fmtPrice(it.stoploss)}</span>
                    </td>
                    <td>
                      <button
                        className={`eva-status ${it.isPublished ? 'eva-status--on' : 'eva-status--off'}`}
                        onClick={() => onTogglePublish(it)}
                        title="Click to toggle"
                      >
                        {it.isPublished ? '● Live' : '○ Draft'}
                      </button>
                    </td>
                    <td className="eva-mono">{fmtDate(it.createdAt)}</td>
                    <td className="eva-mono">
                      👁 {it.viewCount || 0} · ⬇ {it.downloadCount || 0}
                    </td>
                    <td>
                      <div className="eva-actions">
                        <button className="eva-btn-icon" onClick={() => startEdit(it)} title="Edit">✏️</button>
                        <button className="eva-btn-icon eva-btn-icon--danger" onClick={() => onDelete(it._id)} title="Delete">🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default ExpertViewAdmin;
